// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useNavigate } from '@tanstack/react-router';

// material ui
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

// api
import {
  type MeterTypeEnum,
  useGetEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyGetQuery,
  usePatchEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyPatchMutation,
} from '@/api/generated/dataIntegrations/endpoints/dataIntegrations';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// components
import { supplierOptions } from '@/features/eforsyning-integration/eforsyningSupplierOptions';
import { showSnackbar } from '@/redux/snackbarSlice';

export const Route = createFileRoute(
  '/$accountId/_user/eforsyning-integration/$integrationId/edit',
)({
  component: RouteComponent,
});

const meterTypeOptions: MeterTypeEnum[] = ['HeatMeter', 'WaterMeter'];

type FormValues = {
  password: string;
  active: boolean;
  meterType: MeterTypeEnum;
  supplierId: string;
  reCalcName: string;
};

type SupplierOption = {
  value: string;
  label: string;
};

const normalizeSupplierId = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const getSupplierOptionValue = (option: unknown) =>
  normalizeSupplierId(
    (option as { value?: string; id?: string; supplierId?: string })?.value ??
      (option as { value?: string; id?: string; supplierId?: string })?.id ??
      (option as { value?: string; id?: string; supplierId?: string })
        ?.supplierId,
  );

const getSupplierOptionLabel = (option: unknown) =>
  String(
    (option as { label?: string; name?: string; text?: string })?.label ??
      (option as { label?: string; name?: string; text?: string })?.name ??
      (option as { label?: string; name?: string; text?: string })?.text ??
      getSupplierOptionValue(option),
  );

const supplierAutocompleteOptions: SupplierOption[] = supplierOptions.map(
  (option) => ({
    value: getSupplierOptionValue(option),
    label: getSupplierOptionLabel(option),
  }),
);

type EditFormProps = {
  accountIdAsNumber: number;
  rowKey: string;
  integration: {
    Password?: string | null;
    Active?: boolean | null;
    MeterType?: string | null;
    SupplierId?: string | null;
    ReCalcName?: string | null;
  };
  onCancel: () => void;
};

function EditForm({
  accountIdAsNumber,
  rowKey,
  integration,
  onCancel,
}: EditFormProps) {
  const { t } = useTranslation(['eforsyning-integration', 'common']);
  const dispatch = useDispatch();

  const [patchMetadataRow, { isLoading: isSaving }] =
    usePatchEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyPatchMutation();

  const [formValues, setFormValues] = useState<FormValues>({
    password: integration.Password ?? '',
    active: Boolean(integration.Active),
    meterType:
      integration.MeterType === 'WaterMeter' ? 'WaterMeter' : 'HeatMeter',
    supplierId: normalizeSupplierId(integration.SupplierId),
    reCalcName: integration.ReCalcName ?? '',
  });

  const selectedSupplierOption =
    supplierAutocompleteOptions.find(
      (option) => option.value === formValues.supplierId,
    ) ??
    (formValues.supplierId
      ? {
          value: formValues.supplierId,
          label: formValues.supplierId,
        }
      : null);

  const handleFieldChange = <K extends keyof FormValues>(
    field: K,
    value: FormValues[K],
  ) => {
    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (
      !formValues.password.trim() ||
      !formValues.supplierId.trim() ||
      !formValues.reCalcName.trim()
    ) {
      dispatch(
        showSnackbar({
          message: t('createValidationError', {
            defaultValue: 'Please fill in all required fields.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    const result = await patchMetadataRow({
      partitionKey: accountIdAsNumber,
      rowKey,
      eforsyningMetadataPatch: {
        Password: formValues.password,
        Active: formValues.active,
        MeterType: formValues.meterType,
        SupplierId: normalizeSupplierId(formValues.supplierId),
        ReCalcName: formValues.reCalcName.trim(),
        ReCalcAccountId: accountIdAsNumber,
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('updateSuccess', {
            defaultValue: 'Eforsyning integration updated successfully.',
          }),
          severity: 'success',
        }),
      );

      onCancel();
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Stack
      spacing={2}
      mb={2}
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSave();
      }}
    >
      <Card>
        <CardHeader
          title={t('general', {
            defaultValue: 'Eforsyning metadata',
          })}
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          slotProps={{
            title: { variant: 'subtitle1' }
          }}
        />
        <CardContent>
          <Stack spacing={2}>
            <Box
              display="grid"
              gap={2}
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            >
              <TextField
                label={t('username', {
                  defaultValue: 'Username',
                })}
                value={rowKey}
                disabled
                fullWidth
              />
              <TextField
                label={t('password', {
                  defaultValue: 'Password',
                })}
                value={formValues.password}
                onChange={(event) =>
                  handleFieldChange('password', event.target.value)
                }
                required
                type="password"
                fullWidth
              />
            </Box>

            <Box
              display="grid"
              gap={2}
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            >
              <Autocomplete
                options={supplierAutocompleteOptions}
                value={selectedSupplierOption}
                onChange={(_, option) =>
                  handleFieldChange('supplierId', option?.value ?? '')
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => `${option.label}`}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('supplierId', {
                      defaultValue: 'SupplierId',
                    })}
                    required
                  />
                )}
              />
              <TextField
                label={t('reCalcName', {
                  defaultValue: 'ReCalcName',
                })}
                value={formValues.reCalcName}
                onChange={(event) =>
                  handleFieldChange('reCalcName', event.target.value)
                }
                required
                fullWidth
              />
            </Box>

            <Box
              display="grid"
              gap={2}
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            >
              <TextField
                select
                label={t('meterType', {
                  defaultValue: 'MeterType',
                })}
                value={formValues.meterType}
                onChange={(event) =>
                  handleFieldChange(
                    'meterType',
                    event.target.value as MeterTypeEnum,
                  )
                }
                required
                fullWidth
              >
                {meterTypeOptions.map((meterType) => (
                  <MenuItem key={meterType} value={meterType}>
                    {t(`meterTypes.${meterType}`, {
                      defaultValue: meterType,
                    })}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              select
              label={t('activeLabel', {
                defaultValue: 'State',
              })}
              value={formValues.active ? 'true' : 'false'}
              onChange={(event) =>
                handleFieldChange('active', event.target.value === 'true')
              }
              fullWidth
            >
              <MenuItem value="true">
                {t('active', { defaultValue: 'Active' })}
              </MenuItem>
              <MenuItem value="false">
                {t('inactive', {
                  defaultValue: 'Inactive',
                })}
              </MenuItem>
            </TextField>
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>
            {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" type="submit" disabled={isSaving}>
            {isSaving
              ? t('actions.saving', { ns: 'common', defaultValue: 'Saving...' })
              : t('actions.save', { ns: 'common', defaultValue: 'Save' })}
          </Button>
        </CardActions>
      </Card>
    </Stack>
  );
}

function RouteComponent() {
  const { t } = useTranslation('eforsyning-integration');
  const navigate = useNavigate();
  const { accountId, integrationId } = Route.useParams();

  const accountIdAsNumber = Number(accountId);
  const rowKey = decodeURIComponent(integrationId);
  const isAccountIdValid = Number.isFinite(accountIdAsNumber);
  const isRowKeyValid = Boolean(rowKey);

  const {
    data: integration,
    isLoading,
    isFetching,
    isError,
  } = useGetEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyGetQuery(
    {
      partitionKey: accountIdAsNumber,
      rowKey,
    },
    {
      skip: !isAccountIdValid || !isRowKeyValid,
    },
  );

  const isBusy = isLoading || isFetching;

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="eforsyning-metadata-edit-container"
    >
      <PageHeader
        title={t('editTitle', {
          defaultValue: 'Edit Eforsyning integration',
        })}
        to="../../"
      />

      <LinearLoadingIndicator isLoading={isBusy} />

      {!isAccountIdValid || !isRowKeyValid ? (
        <Alert severity="error">
          {t('accountIdUrlInvalid', {
            defaultValue: 'The account id in the URL is invalid.',
          })}
        </Alert>
      ) : null}

      {isError ? (
        <Alert severity="error">
          {t('loadError', {
            defaultValue: 'Failed to load Eforsyning integration.',
          })}
        </Alert>
      ) : null}

      {integration ? (
        <EditForm
          key={`${accountIdAsNumber}-${rowKey}`}
          accountIdAsNumber={accountIdAsNumber}
          rowKey={rowKey}
          integration={integration}
          onCancel={() => navigate({ to: '../../' })}
        />
      ) : null}
    </PageContainer>
  );
}
